import { useCallback, useEffect, useState } from 'react';
import { useRumError } from 'services/observability/datadog';

/**
 * Helper function to get the value from local storage.
 *
 * @param {string} key - The key of the item in local storage.
 * @param {StoredValueType} defaultValue - The default value to use if the item is not found or there is an error.
 * @param {Function} addErrorSignal - Function to log errors.
 * @returns {StoredValueType} - The value from local storage or the default value.
 */
const getItem = <StoredValueType>(
  key: string,
  defaultValue: StoredValueType,
  addErrorSignal: (error: Error, message: string, context: { key: string; error: string }) => void,
): StoredValueType => {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : `Failed to parse localStorage item for key ${key}`;
    addErrorSignal(new Error(errorMessage), `GET_LOCAL_STORAGE_${key.toUpperCase()}`, {
      key,
      error: errorMessage,
    });
    return defaultValue;
  }
};

/**
 * Sets/gets a value from local storage.
 *
 * @param key - The key of the item in local storage.
 * @param defaultValue - The default value to use if the item is not found or there is an error.
 *
 * @example
 * const [val, setVal] = useLocalStorage('count', 1)
 * setVal(2);    // allowed, because the type of `1` is number
 * setVal('two') // error, because `'two'` is not of type number
 *
 * const [val, setVal] = useLocalStorage('nameList', []);
 * setVal(['justin']); // errors because type inferred to be `never[]`
 * fix with: const [val, setVal] = useLocalStorage<string[]>('nameList', []);
 *
 * type AllowedCounts = 1 | 2 | 3 | 4; // create a type with a narrower range
 * const [val, setVal] = useLocalStorage<AllowedCounts>('count', 1)
 * setVal(2);    // allowed, because `2` is an AllowedCount
 * setVal(5) // error, because 5 is out of range
 *
 */
export const useLocalStorage = <StoredValueType>(key: string, defaultValue: StoredValueType) => {
  const addErrorSignal = useRumError();
  // pass state once initialized
  const [storedValue, setStoredValue] = useState<StoredValueType>(() => {
    return getItem(key, defaultValue, addErrorSignal);
  });

  const getLocalStorageValue = useCallback((): StoredValueType => {
    return getItem(key, defaultValue, addErrorSignal);
  }, [key, defaultValue, addErrorSignal]);

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : `Failed to set localStorage item for key ${key}`;
      addErrorSignal(new Error(errorMessage), `SET_LOCAL_STORAGE_${key.toUpperCase()}`, {
        key,
        error: errorMessage,
      });
    }
  }, [storedValue]);

  return [storedValue, setStoredValue, getLocalStorageValue] as const;
};
